import React, {useState, useEffect, useRef} from 'react';
import { Typography } from '@material-ui/core';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import HighchartsExport from 'highcharts/modules/exporting';
import HighchartsExportData from 'highcharts/modules/export-data';
import moment from 'moment';
import 'moment/locale/th';

HighchartsExport(Highcharts);
HighchartsExportData(Highcharts);

const RRIStationChart = React.forwardRef(function({ stationName, type }, ref) {
  const [chartData, setChartData] = React.useState([]);
  const [chartLoading, setChartLoading] = React.useState(false);
  const [thresholds, setThresholds] = useState(null);

  React.useEffect(() => {
    if (!stationName) return;
    const url = `${process.env.REACT_APP_LIVE_HII}/product/latest/rri/station/${stationName}.csv`;
    setChartLoading(true);

    fetch(url)
      .then(res => res.text())
      .then(text => {
        const lines = text.trim().split('\n');
        const parsed = lines.slice(1)
          .map(line => {
            const cols = line.split(',');
            const ts = moment(cols[0], 'YYYY-MM-DD HH:mm:ss').valueOf();
            return {
              ts,
              hr: parseFloat(cols[1]),
              qr: parseFloat(cols[2]),
              qrMCM: parseFloat(cols[3]),
            };
          })
          .filter(d => !isNaN(d.ts));
        setChartData(parsed);
      })
      .catch(() => setChartData([]))
      .finally(() => setChartLoading(false));
  }, [stationName]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_LIVE_HII}/product/latest/rri/metadata/csv/rri_station_coordinate_qr.csv`)
      .then(res => res.text())
      .then(text => {
        const lines = text.trim().split('\n');
        const found = lines.slice(1).find(line => {
          const cols = line.split(',');
          return cols[1]?.trim() === stationName;
        });
        if (found) {
          const cols = found.split(',');
          setThresholds({
            lowest:   parseFloat(cols[4]),
            alarm:    parseFloat(cols[5]),
            warning:  parseFloat(cols[6]),
            critical: parseFloat(cols[7]),
          });
        }
      })
      .catch(() => {});
  }, [stationName]);

  useEffect(() => {
    const chart = ref?.current?.chart;
    if (chart) {
      setTimeout(() => chart.reflow(), 100);
    }
  }, [chartData]);

  if (chartLoading) {
    return (
      <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 13, color: '#7E8F9F', textAlign: 'center', padding: 16 }}>
        กำลังโหลดข้อมูล...
      </Typography>
    );
  }

  if (chartData.length === 0) {
    return (
      <Typography style={{ fontFamily: 'Noto Sans Thai', fontSize: 13, color: '#7E8F9F', textAlign: 'center', padding: 16 }}>
        ไม่มีข้อมูล
      </Typography>
    );
  }
  

  const isReservoir = type === 'reservoir';
  const seriesData = chartData.map(d => [d.ts, isReservoir ? d.qrMCM : d.qr]);
  const seriesName = isReservoir ? 'ปริมาณน้ำไหลลงอ่าง' : 'ปริมาณน้ำท่า';
  const unit = isReservoir ? 'ล้าน ลบ.ม.' : 'ลบ.ม/วินาที'
  const futureColor = '#31985F';
  const futureFill = '#ECF5F0';

  // เส้นแบ่ง ณ เวลาปัจจุบัน
  const nowTs = moment().valueOf();

  const options = {
    title: { text: '' },
    chart: {
    zoomType: 'x',
    height: 300,
    marginLeft: 55,
    marginRight: 10,
    spacingLeft: 0,
    spacingRight: 0,
    resetZoomButton: {
        theme: { display: 'none' },
    },
    style: { fontFamily: 'Noto Sans Thai' },
    },
    plotOptions: {
      line: { turboThreshold: 0 },
      series: {
        showInNavigator: true,
        gapSize: 6,
      },
    },
    scrollbar: { enabled: true },
    navigator: {
        enabled: true,
        adaptToUpdatedData: false,
        xAxis: {
            tickInterval: 24 * 3600 * 1000,
            labels: {
            formatter() {
                return moment(this.value).locale('th').format('DD MMM');
            },
            style: { fontFamily: 'Noto Sans Thai', fontSize: '10px' },
            },
        },
    },
    xAxis: {
      type: 'datetime',
      tickInterval: 24 * 3600 * 1000,
      units: [['day', [1]]],
      labels: {
        formatter() {
          return moment(this.value).locale('th').format('DD MMM');
        },
        style: { fontFamily: 'Noto Sans Thai', fontSize: '11px' },
      },
      plotLines: [
        {
          color: '#000000',
          width: 2,
          value: nowTs,
          zIndex: 5,
          label: {
            text: 'ปัจจุบัน',
            style: { fontFamily: 'Noto Sans Thai', fontSize: '11px' },
          },
        },
      ],
    },
    yAxis: {
      opposite: false,
      title: { text: null },
      labels: {
        format: '{value:.2f}',
        style: { fontFamily: 'Noto Sans Thai' },
      },
      plotLines: thresholds ? [
        !isNaN(thresholds.lowest) && {
          value: thresholds.lowest,
          color: '#0F08D1',
          width: 2,
          dashStyle: 'Dash',
          zIndex: 5,
          label: { text: `ความจุต่ำสุด (${thresholds.lowest}) ลบ.ม / วินาที`, style: { color: '#0F08D1', fontFamily: 'Noto Sans Thai', fontSize: '10px' } },
        },
        !isNaN(thresholds.alarm) && {
          value: thresholds.alarm,
          color: '#DFC328',
          width: 2,
          dashStyle: 'Dash',
          zIndex: 5,
          label: { text: `เฝ้าระวัง (${thresholds.alarm}) ลบ.ม / วินาที`, style: { color: '#DFC328', fontFamily: 'Noto Sans Thai', fontSize: '10px' } },
        },
        !isNaN(thresholds.warning) && {
          value: thresholds.warning,
          color: '#ED9B4D',
          width: 2,
          dashStyle: 'Dash',
          zIndex: 5,
          label: { text: `เตือนภัย (${thresholds.warning}) ลบ.ม / วินาที`, style: { color: '#ED9B4D', fontFamily: 'Noto Sans Thai', fontSize: '10px' } },
        },
        !isNaN(thresholds.critical) && {
          value: thresholds.critical,
          color: '#C83C35',
          width: 2,
          dashStyle: 'Dash',
          zIndex: 5,
          label: { text: `วิกฤต (${thresholds.critical}) ลบ.ม / วินาที`, style: { color: '#C83C35', fontFamily: 'Noto Sans Thai', fontSize: '10px' } },
        },
      ].filter(Boolean) : [],
    },
    tooltip: {
      crosshairs: true,
      shared: true,
      formatter() {
        const date = moment(this.x).locale('th').format('DD MMM HH:mm');
        const val = this.y?.toFixed(3);
        return `<b>${date}</b><br/>${seriesName}: <b>${val}</b> ${unit}`;
      },
    },
    legend: { enabled: false },
    series: [
      {
        name: seriesName,
        type: 'areaspline',
        zoneAxis: 'x',
        zones: [
        // {
        //     value: nowTs,
        //     color: '#000000',
        //     fillColor: 'transparent',
        // },
        {
          color: isReservoir ? '#df5745' : '#349961',
          fillColor: isReservoir ? '#fcefed' : '#ecf5f0',
        },
        ],
        data: seriesData,
      },
    ],
    exporting: {
      enabled: true,
      buttons: {
        contextButton: {
          enabled: false,
          menuItems: ['downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG'],
          align: 'left',
          y: -10,
          x: -10,
        },
      },
    },
    credits: { enabled: false },
  };

  return (
    <HighchartsReact
      highcharts={Highcharts}
      constructorType="chart"
      options={options}
      ref={ref}
    />
  );
});
export default RRIStationChart;